import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import type { TenantPlaidItem, TenantPlaidAccount, TenantSyncCursor } from '@/lib/db-tenant'
import { useOrganization } from '@/contexts/OrganizationContext'

/** Stable empty object for default filters — avoids new reference on each render */
const EMPTY_FILTERS = {} as const

/** Safety timeout for sync polling — 120 seconds */
const SYNC_POLLING_TIMEOUT_MS = 120_000

/** Polling interval when sync is in progress — 3 seconds */
const SYNC_POLLING_INTERVAL_MS = 3_000

/**
 * Grace period after a sync trigger — poll for up to 15s waiting for
 * the Trigger.dev task to start and set sync_status = 'syncing' in DB.
 * Without this, there's a gap between the trigger and when polling starts.
 */
const TRIGGER_GRACE_PERIOD_MS = 15_000

/**
 * Query keys for Plaid-related data
 * All keys include organizationId to ensure proper cache isolation
 */
export const PLAID_KEYS = {
  items: (organizationId: string | undefined) => ['plaid', 'items', organizationId] as const,
  itemsByFacility: (organizationId: string | undefined, facilityId: string) => ['plaid', 'items', 'facility', organizationId, facilityId] as const,
  item: (organizationId: string | undefined, id: string) => ['plaid', 'items', organizationId, id] as const,
  accounts: (organizationId: string | undefined) => ['plaid', 'accounts', organizationId] as const,
  accountsByFacility: (organizationId: string | undefined, facilityId: string) => ['plaid', 'accounts', 'facility', organizationId, facilityId] as const,
  accountsByItem: (organizationId: string | undefined, itemId: string) => ['plaid', 'accounts', organizationId, itemId] as const,
  transactions: (organizationId: string | undefined, filters: Record<string, unknown>) =>
    ['plaid', 'transactions', organizationId, filters] as const,
  transactionCount: (organizationId: string | undefined, filters: Record<string, unknown>) =>
    ['plaid', 'transactions', 'count', organizationId, filters] as const,
  syncStatus: (organizationId: string | undefined) => ['plaid', 'sync-status', organizationId] as const,
  /**
   * Stores the timestamp (Date.now()) when a sync was last triggered.
   * Written by useSyncTransactions/useSyncAllTransactions, read by useSyncStatus
   * to keep polling during the grace period before the Trigger.dev task starts.
   * Using queryClient instead of a module-level variable keeps the state
   * React-idiomatic and scoped to the organization.
   */
  syncTriggerTime: (organizationId: string | undefined) => ['plaid', 'sync-trigger-time', organizationId] as const,
  categories: (organizationId: string | undefined, filters: Record<string, unknown>) =>
    ['plaid', 'categories', organizationId, filters] as const,
}

/**
 * Hook to monitor Plaid sync status across all items.
 * Polls plaid_sync_cursors when any item has sync_status = 'syncing'.
 * Auto-stops polling when all items complete or fail.
 * Handles page leave/return — detects 'syncing' on mount and resumes polling.
 *
 * When sync transitions from 'syncing' to 'completed'/'failed':
 * - Shows a toast with the result
 * - Invalidates plaid queries so data refreshes
 *
 * Safety timeout: after 120s of continuous polling, stops and shows warning.
 *
 * @returns isSyncing — true when any cursor has sync_status = 'syncing'
 * @returns cursors — current sync cursor data
 * @returns error — query error if fetching cursors failed
 */
export function useSyncStatus() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()

  /**
   * Map of plaid_item_id -> sync_status from the previous render.
   * Used to detect transitions to terminal states (completed/failed)
   * regardless of what the previous state was (covers fast tasks
   * that skip from idle straight to completed between polls).
   */
  const prevStatusMapRef = useRef<Map<string, string>>(new Map())

  /**
   * Timestamp when polling started — used for the 120s safety timeout.
   * Null when not actively polling.
   */
  const pollingStartRef = useRef<number | null>(null)

  /**
   * Whether the safety timeout has fired for the current polling session.
   * Prevents duplicate timeout warnings.
   */
  const timeoutFiredRef = useRef(false)

  /**
   * Skip transition detection on the first data load — cursors may already
   * have terminal states (completed/failed) from a previous sync session.
   */
  const isFirstLoadRef = useRef(true)

  const query = useQuery<TenantSyncCursor[]>({
    queryKey: PLAID_KEYS.syncStatus(currentOrganization?.id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidSyncCursors.list()
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
    // Enable polling when any cursor is syncing; otherwise fetch only on demand.
    // Also polls during the trigger grace period (task hasn't started yet).
    // Stops polling if the safety timeout has fired (timeoutFiredRef).
    refetchInterval: (query) => {
      if (timeoutFiredRef.current) return false
      const cursors = query.state.data
      if (!cursors) return false

      const anySyncing = cursors.some((c) => c.sync_status === 'syncing')

      // Task is running — clear the trigger timestamp and poll normally
      if (anySyncing) {
        queryClient.removeQueries({ queryKey: PLAID_KEYS.syncTriggerTime(currentOrganization?.id) })
        return SYNC_POLLING_INTERVAL_MS
      }

      // No cursor is syncing yet, but a trigger was recently fired —
      // keep polling during the grace period so we catch when the task starts
      const triggerTime = queryClient.getQueryData<number>(
        PLAID_KEYS.syncTriggerTime(currentOrganization?.id)
      )
      if (triggerTime !== undefined) {
        const elapsed = Date.now() - triggerTime
        if (elapsed < TRIGGER_GRACE_PERIOD_MS) {
          return SYNC_POLLING_INTERVAL_MS
        }
        // Grace period expired — stop forced polling
        queryClient.removeQueries({ queryKey: PLAID_KEYS.syncTriggerTime(currentOrganization?.id) })
      }

      return false
    },
  })

  /** Memoize cursors to maintain referential stability for useEffect dependency */
  const cursors = useMemo(() => query.data ?? [], [query.data])
  const isSyncing = cursors.some((c) => c.sync_status === 'syncing')

  // Handle polling timeout and sync completion transitions
  useEffect(() => {
    // Track polling start time for the safety timeout
    if (isSyncing && pollingStartRef.current === null) {
      pollingStartRef.current = Date.now()
      timeoutFiredRef.current = false
    }

    // Safety timeout: stop polling after 120s and warn the user
    if (isSyncing && pollingStartRef.current !== null && !timeoutFiredRef.current) {
      const elapsed = Date.now() - pollingStartRef.current
      if (elapsed >= SYNC_POLLING_TIMEOUT_MS) {
        timeoutFiredRef.current = true
        toast.error('Sync is taking longer than expected. Please refresh the page to check status.')
        pollingStartRef.current = null
        return
      }
    }

    // On first data load, just snapshot current statuses — don't detect transitions.
    // Cursors may already have terminal states from a previous sync session.
    if (isFirstLoadRef.current && cursors.length > 0) {
      isFirstLoadRef.current = false
      const initialMap = new Map<string, string>()
      for (const cursor of cursors) {
        initialMap.set(cursor.plaid_item_id, cursor.sync_status)
      }
      prevStatusMapRef.current = initialMap
      return
    }

    // Detect cursors that just transitioned to a terminal state (completed/failed).
    // Compares current statuses with previous render — works even if task
    // completes so fast we never observe 'syncing' (idle → completed between polls).
    const newlyCompleted: TenantSyncCursor[] = []
    const newlyFailed: TenantSyncCursor[] = []

    for (const cursor of cursors) {
      const prevStatus = prevStatusMapRef.current.get(cursor.plaid_item_id)
      if (cursor.sync_status === 'completed' && prevStatus !== 'completed') {
        newlyCompleted.push(cursor)
      }
      if (cursor.sync_status === 'failed' && prevStatus !== 'failed') {
        newlyFailed.push(cursor)
      }
    }

    const hasTransitions = newlyCompleted.length > 0 || newlyFailed.length > 0

    if (hasTransitions) {
      // Clear trigger timestamp and polling tracking
      queryClient.removeQueries({ queryKey: PLAID_KEYS.syncTriggerTime(currentOrganization?.id) })
      pollingStartRef.current = null
      timeoutFiredRef.current = false

      // Show toast for the transition
      if (newlyFailed.length > 0) {
        toast.error(newlyFailed[0].last_error || 'Failed to sync transactions')
      } else {
        toast.success('Transactions synced successfully')
      }

      // Invalidate plaid data queries so items / accounts / transactions refresh
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plaid', 'items'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] }),
      ])
    }

    // Reset polling tracking when no longer syncing (covers non-transition case)
    if (!isSyncing) {
      pollingStartRef.current = null
    }

    // Save current statuses for next comparison
    const nextMap = new Map<string, string>()
    for (const cursor of cursors) {
      nextMap.set(cursor.plaid_item_id, cursor.sync_status)
    }
    prevStatusMapRef.current = nextMap
  }, [isSyncing, cursors, queryClient, currentOrganization?.id])

  return {
    isSyncing,
    cursors,
    error: query.error,
  }
}

/**
 * Hook to fetch all connected Plaid Items (bank connections).
 * Uses tenant-specific database client — no organizationId filtering in queries.
 */
export function usePlaidItems() {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidItem[]>({
    queryKey: PLAID_KEYS.items(currentOrganization?.id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidItems.list()
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch Plaid Items for a specific facility.
 * Uses tenant-specific database client.
 */
export function usePlaidItemsByFacility(facilityId: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidItem[]>({
    queryKey: PLAID_KEYS.itemsByFacility(currentOrganization?.id, facilityId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidItems.listByFacility(facilityId)
      if (error) throw error
      return data
    },
    enabled: !!facilityId && !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch a single Plaid Item by ID.
 * Uses tenant-specific database client.
 */
export function usePlaidItem(id: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidItem | null>({
    queryKey: PLAID_KEYS.item(currentOrganization?.id, id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidItems.getById(id)
      if (error) throw error
      return data
    },
    enabled: !!id && !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch all Plaid accounts.
 * Uses tenant-specific database client.
 */
export function usePlaidAccounts() {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidAccount[]>({
    queryKey: PLAID_KEYS.accounts(currentOrganization?.id),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      if (import.meta.env.DEV) {
        console.log('Fetching Plaid accounts from tenant DB...')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidAccounts.list()
      if (import.meta.env.DEV) {
        console.log('Plaid accounts result:', { data, error })
      }
      if (error) {
        console.error('Failed to fetch Plaid accounts:', error)
        throw error
      }
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch Plaid accounts for a specific facility.
 * Uses tenant-specific database client.
 */
export function usePlaidAccountsByFacility(facilityId: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidAccount[]>({
    queryKey: PLAID_KEYS.accountsByFacility(currentOrganization?.id, facilityId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidAccounts.listByFacility(facilityId)
      if (error) throw error
      return data
    },
    enabled: !!facilityId && !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch accounts for a specific Plaid Item.
 * Uses tenant-specific database client.
 */
export function usePlaidAccountsByItem(itemId: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<TenantPlaidAccount[]>({
    queryKey: PLAID_KEYS.accountsByItem(currentOrganization?.id, itemId),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.plaidAccounts.listByItem(itemId)
      if (error) throw error
      return data
    },
    enabled: !!itemId && !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch transactions with optional filters.
 * Uses tenant-specific database client — no organizationId filter needed.
 * Array filters (facilityIds, accountIds, categories) match the db-tenant API directly.
 * Security note: accountIds are validated server-side via RLS — only accounts
 * belonging to the current organization are accessible through the tenant client.
 */
export function useBankTransactions(filters?: {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  accountIds?: string[]
  categories?: string[]
  search?: string
  limit?: number
  offset?: number
}) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    // Cache keys kept under legacy 'plaid' namespace intentionally — other
    // code and an invalidation in useBanking.ts key off ['plaid','transactions'].
    queryKey: PLAID_KEYS.transactions(currentOrganization?.id, filters ?? EMPTY_FILTERS),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankTransactions.list(filters)
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to get total transaction count for pagination.
 * Uses tenant-specific database client — no organizationId filter needed.
 * Array filters (facilityIds, accountIds, categories) match the db-tenant API directly.
 */
export function useBankTransactionCount(filters?: {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  accountIds?: string[]
  categories?: string[]
  search?: string
}) {
  const { currentOrganization } = useOrganization()
  return useQuery({
    // Cache keys kept under legacy 'plaid' namespace intentionally — other
    // code and an invalidation in useBanking.ts key off ['plaid','transactions'].
    queryKey: PLAID_KEYS.transactionCount(currentOrganization?.id, filters ?? EMPTY_FILTERS),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankTransactions.count(filters)
      if (error) throw error
      return data
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to fetch distinct category options for the dashboard filter dropdown.
 *
 * Source-agnostic (DCP-223): the underlying `distinctCategories` merges
 * user-defined `categories` (all sources) with legacy `plaid_category` keys.
 * `useBankTransactions` / `useBankTransactionCount` are likewise
 * source-agnostic (whereas `usePlaidAccounts` is genuinely Plaid-only).
 *
 * Filters by date range, facility, and account — intentionally excludes
 * the categories filter to avoid hiding unselected categories from the dropdown.
 * Returns options ready for MultiSelect: Array<{ value: string; label: string }>
 */
export function useBankTransactionCategories(filters?: {
  startDate?: string
  endDate?: string
  facilityIds?: string[]
  accountIds?: string[]
}) {
  const { currentOrganization } = useOrganization()
  const stableFilters = filters ?? EMPTY_FILTERS
  return useQuery({
    // Cache key kept under legacy 'plaid' namespace intentionally — other
    // code and an invalidation in useBanking.ts key off ['plaid','categories'].
    queryKey: PLAID_KEYS.categories(currentOrganization?.id, stableFilters),
    queryFn: async () => {
      const { createTenantDb } = await import('@/lib/db-tenant')

      if (!currentOrganization?.id) {
        throw new Error('No organization selected for tenant database access')
      }

      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.bankTransactions.distinctCategories(filters)
      if (error) throw error
      // distinctCategories now returns source-agnostic { value, label } options directly
      return data || []
    },
    enabled: !!currentOrganization?.id,
  })
}

/**
 * Hook to create a Plaid Link token
 */
export function useCreateLinkToken() {
  return useMutation({
    mutationFn: async (params?: { facilityId?: string }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'create-link-token',
          products: ['transactions'],
          facilityId: params?.facilityId,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to create link token')
      }

      return response.json()
    },
  })
}


/**
 * Calls the plaid-link edge function with authentication and error handling.
 * Shared by useCreateUpdateLinkToken, useCompleteUpdate, and other plaid-link actions.
 *
 * @param action - The edge function action (e.g. 'create-link-token-update', 'complete-update')
 * @param payload - Additional fields to include in the request body
 * @returns Parsed JSON response from the edge function
 */
async function callPlaidLinkFunction<T = unknown>(action: string, payload: Record<string, unknown>): Promise<T> {
  const supabase = getSupabaseClient()
  const { data: { session } } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error('Authentication required - please log in')
  }

  const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || `Failed to ${action}`)
  }

  return response.json() as Promise<T>
}

/**
 * Creates a link token in update mode for re-authenticating an existing Plaid Item.
 * Used when item status is 'error' (credentials expired, login required, etc.).
 *
 * @returns mutation — call with `{ itemId: string }` to fetch an update-mode link token
 *   Response: `{ link_token: string, expiration: string }`
 */
export function useCreateUpdateLinkToken() {
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { itemId: string; organizationId?: string }) =>
      callPlaidLinkFunction<{ link_token: string; expiration: string }>('create-link-token-update', {
        itemId: params.itemId,
        organizationId: params.organizationId || currentOrganization?.id,
      }),
  })
}

/**
 * Marks a Plaid Item as successfully re-authenticated after update mode completion.
 * Resets item status to 'connected' and clears the error_code field.
 *
 * @returns mutation — call with `{ itemId: string }` after Plaid Link update mode succeeds
 */
export function useCompleteUpdate() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { itemId: string; organizationId?: string }) =>
      callPlaidLinkFunction('complete-update', {
        itemId: params.itemId,
        organizationId: params.organizationId || currentOrganization?.id,
      }),
    onSuccess: async () => {
      // Invalidate items and accounts so the status badge refreshes
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plaid', 'items'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'accounts'] }),
      ])
    },
  })
}

/**
 * Hook to connect a new bank (exchange token)
 */
export function useConnectBank() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      publicToken: string
      institutionId: string | null
      institutionName: string | null
      organizationId?: string
      facilityId?: string
    }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'exchange-token',
          ...params,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to connect bank')
      }

      return response.json()
    },
    onSuccess: async () => {
      // Invalidate all plaid items and accounts queries using prefix matching.
      // queryKey prefix matching covers all sub-keys (by facility, by item, etc.)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plaid', 'items'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'accounts'] }),
      ])
    },
  })
}

/**
 * Hook to disconnect a bank
 */
export function useDisconnectBank() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { itemId: string; organizationId?: string; facilityId?: string }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'remove-item',
          ...params,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to disconnect bank')
      }

      return response.json()
    },
    onSuccess: async () => {
      // Invalidate all plaid queries using prefix matching
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plaid', 'items'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] }),
      ])
    },
  })
}

/**
 * Hook to trigger transaction sync for a specific Plaid Item.
 * Sends an async trigger to the Edge Function which delegates to Trigger.dev.
 * Returns immediately with { triggered: true } — actual sync progress is
 * monitored by the useSyncStatus hook via plaid_sync_cursors polling.
 *
 * The mutation's isPending only covers the initial trigger request (<1s).
 * For ongoing sync status, components should use useSyncStatus().isSyncing.
 */
export function useSyncTransactions() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params: { itemId: string; organizationId?: string; facilityId?: string }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'sync-transactions',
          ...params,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to start sync')
      }

      return response.json() as Promise<{ triggered: boolean; itemId: string }>
    },
    onSuccess: () => {
      // Record trigger time in queryClient so useSyncStatus begins eager polling immediately.
      // This bridges the gap between the trigger request and when the Trigger.dev task
      // sets sync_status = 'syncing' in the database.
      queryClient.setQueryData(
        PLAID_KEYS.syncTriggerTime(currentOrganization?.id),
        Date.now()
      )
      void queryClient.invalidateQueries({ queryKey: ['plaid', 'sync-status'] })
      toast.success('Sync started')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start sync')
    },
  })
}

/**
 * Hook to trigger transaction sync for ALL connected Plaid Items.
 * Sends an async trigger to the Edge Function which delegates to Trigger.dev.
 * Returns immediately with { triggered: true } — actual sync progress is
 * monitored by the useSyncStatus hook via plaid_sync_cursors polling.
 *
 * The mutation's isPending only covers the initial trigger request (<1s).
 * For ongoing sync status, components should use useSyncStatus().isSyncing.
 */
export function useSyncAllTransactions() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()

  return useMutation({
    mutationFn: async (params?: { organizationId?: string }) => {
      const supabase = getSupabaseClient()
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('Authentication required - please log in')
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/plaid-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'sync-all',
          ...params,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to start sync')
      }

      return response.json() as Promise<{ triggered: boolean }>
    },
    onSuccess: () => {
      // Record trigger time in queryClient so useSyncStatus begins eager polling immediately.
      // This bridges the gap between the trigger request and when the Trigger.dev task
      // sets sync_status = 'syncing' in the database.
      queryClient.setQueryData(
        PLAID_KEYS.syncTriggerTime(currentOrganization?.id),
        Date.now()
      )
      void queryClient.invalidateQueries({ queryKey: ['plaid', 'sync-status'] })
      toast.success('Sync started for all banks')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start sync')
    },
  })
}
